import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import debounce from 'lodash/debounce';
import { searchService } from '../services/searchService';

const SearchBar = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const searchRef = useRef(null);

    // Handle click outside to close results
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowResults(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSearch = async (searchQuery) => {
        if (!searchQuery.trim()) {
            setResults([]);
            return;
        }

        setIsLoading(true);
        try {
            const searchResults = await searchService.search(searchQuery);
            setResults(searchResults);
        } catch (error) {
            console.error('Search error:', error);
            setResults([]);
        } finally {
            setIsLoading(false);
        }
    };

    const debouncedSearch = debounce(handleSearch, 300);

    const handleInputChange = (e) => {
        const value = e.target.value;
        setQuery(value);
        setShowResults(true);
        debouncedSearch(value);
    };

    const handleResultClick = (result) => {
        setQuery(result.entity);
        setShowResults(false);
        // TODO: Add navigation to the entity's page
        // navigate(`/entity/${result.uniqueId}`);
    };

    return (
        <div className="relative w-full max-w-2xl mx-auto" ref={searchRef}>
            <div className="relative">
                <input
                    type="text"
                    value={query}
                    onChange={handleInputChange}
                    placeholder={t('search')}
                    className="w-full px-4 py-3 pl-12 text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            </div>

            {showResults && (query.trim() || isLoading) && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto">
                    {isLoading ? (
                        <div className="p-4 text-center text-gray-500">
                            {t('search.loading')}
                        </div>
                    ) : results.length > 0 ? (
                        <ul className="py-1">
                            {results.map((result) => (
                                <li
                                    key={result.uniqueId}
                                    onClick={() => handleResultClick(result)}
                                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                                >
                                    {result.entity}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="p-4 text-center text-gray-500">
                            {t('search.noResults')}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SearchBar; 